"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { addToCart } from "@/store/slices/cartSlice";
import ProductFeaturesSection from "@/components/products/ProductFeaturesSection";
import ProductSuitabilitySection from "@/components/products/ProductSuitabilitySection";
import ProductInformationTabs from "@/components/products/ProductInformationTabs";
import ProcessSection from "@/components/ProcessSection";
import ProductBlogSection from "@/components/blog/ProductBlogSection";
import checked from "@/assets/checked.svg";
import cartWhite from "@/assets/cart-white.svg";
import facebookIcon from "@/assets/fb.svg";
import twitterIcon from "@/assets/twitter.svg";
import pinterestIcon from "@/assets/pinterest.svg";
import linkedinIcon from "@/assets/linkedin.svg";
import {
  ProductData,
  ProductPrices,
  OrderingPathway,
  convertToProduct,
} from "@/types/product";
import {
  LILLYDIRECT_COMMITMENTS,
  LILLYDIRECT_BENEFITS,
} from "@/constants/LillyDirect";
import { BlogPost } from "@/types/blog";

interface ProductDetailClientProps {
  product: ProductData;
  pgdFormRefId: string | null;
  latestPosts?: BlogPost[];
}

export default function ProductDetailClient({
  product,
  pgdFormRefId,
  latestPosts = [],
}: ProductDetailClientProps) {
  const dispatch = useAppDispatch();
  const { items } = useAppSelector((state) => state.cart);

  const [selectedTreatment, setSelectedTreatment] = useState(0);

  /**
   * A £0 amount means "not priced yet" in this catalogue rather than free, the
   * same rule the repeat order page and the products listing apply. Unpriced
   * strengths are dropped here, at the source, so nothing downstream can offer
   * one: they are not counted, not listed, not selectable and not orderable.
   */
  const priced = useCallback(
    (prices: ProductPrices | undefined): ProductPrices => ({
      list: (prices?.list ?? []).filter(
        (item) => Number.isFinite(Number(item.amount)) && Number(item.amount) > 0,
      ),
    }),
    [],
  );

  /**
   * Two-tier ordering. A product can be offered on Standard (flexible)
   * ordering, LillyDirect ordering, or both, each with its own price list.
   * See tasks/lillydirect-two-tier/03-product-page-offer-presentation.md
   *
   * A product with no OrderingOffers widget defaults to Standard only, so
   * every existing product renders exactly as it did before.
   */
  const offers = product.orderingOffers;
  // Needs a priced option, not merely a list. An offer whose every strength is
  // unpriced would otherwise render as selectable and then price at nothing.
  const lillyDirectAvailable =
    offers?.lillydirect === true &&
    priced(product.lillyDirectPrices).list.length > 0;
  const standardAvailable = offers?.standard !== false;
  const showOfferChoice = standardAvailable && lillyDirectAvailable;

  const [selectedOffer, setSelectedOffer] = useState<OrderingPathway>(
    standardAvailable ? "standard" : "lillydirect"
  );

  // The price list the rest of the page reads. Everything below uses this
  // rather than product.productPrices directly, so switching offer reprices
  // the whole page.
  const activePrices = useMemo(
    () =>
      priced(
        selectedOffer === "lillydirect" && lillyDirectAvailable
          ? product.lillyDirectPrices
          : product.productPrices,
      ),
    [
      priced,
      selectedOffer,
      lillyDirectAvailable,
      product.lillyDirectPrices,
      product.productPrices,
    ],
  );

  /**
   * Nothing priced means nothing to sell, so the page stops offering it.
   *
   * This is the same rule the products listing applies to its cards. It is
   * repeated here rather than trusted from there because a product page is
   * reachable directly, from a link or from search, without passing the
   * listing at all.
   */
  const startingPrice = activePrices.list.length
    ? Math.min(...activePrices.list.map((item) => Number(item.amount)))
    : null;
  const isAvailable = startingPrice !== null;

  /**
   * Largest saving for the same dose, so the figure quoted is one a patient can
   * actually get rather than the gap between a 2.5mg and a 15mg.
   */
  const lillyMaxSaving = useMemo(() => {
    if (!lillyDirectAvailable) return null;
    const key = (label: string) => label.trim().toLowerCase().replace(/\s+/g, "");
    let best = 0;
    product.productPrices.list.forEach((standard) => {
      const match = product.lillyDirectPrices.list.find(
        (lilly) => key(lilly.label) === key(standard.label),
      );
      if (match && standard.amount > match.amount) {
        best = Math.max(best, standard.amount - match.amount);
      }
    });
    return best > 0 ? best : null;
  }, [lillyDirectAvailable, product.productPrices, product.lillyDirectPrices]);

  // The two lists can differ in length, so an index valid for one may not be
  // valid for the other. Reset rather than silently showing the wrong strength.
  const handleOfferChange = (offer: OrderingPathway) => {
    setSelectedOffer(offer);
    setSelectedTreatment(0);
  };

  // Carries the chosen offer into the consultation so the choice survives the
  // whole journey.
  const consultationHref = pgdFormRefId
    ? `/consultation/${pgdFormRefId}${
        showOfferChoice || selectedOffer === "lillydirect"
          ? `?offer=${selectedOffer}`
          : ""
      }`
    : "";
  const [quantity, setQuantity] = useState(1);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  const buttonRef = useRef<HTMLDivElement>(null);

  const treatmentOptions = [
    "Weight Control Blood Profile - Cholesterol, HbA1c & Thyroid",
    "Comprehensive Weight Check - Cholesterol, HbA1c & Thyroid",
    "Metabolic Health Blood Test - Cholesterol, HbA1c & Thyroid",
    "Metabolism & Weight Test - Cholesterol, HbA1c & Thyroid",
    "Advanced Weight Screening - Cholesterol, HbA1c & Thyroid",
  ];

  // Check if product is in cart
  useEffect(() => {
    if (product) {
      const isInCart = items.some(
        (item) => item.product.id === product.productId
      );
      setIsAddedToCart(isInCart);
    }
  }, [product, items]);

  // Handle floating button visibility based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

        // Show floating button when main button is not visible and only on mobile/tablet
        setShowFloatingButton(!isVisible && window.innerWidth < 1024);
      }
    };

    handleScroll(); // Check initial state
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const handleAddToCart = () => {
    if (product) {
      /**
       * The price comes from the list actually on screen. selectedTreatment
       * indexes activePrices, which is filtered and may be the LillyDirect
       * list, so letting convertToProduct look the index up in the raw
       * Standard list would cart the wrong price.
       */
      const convertedProduct = convertToProduct(
        product,
        selectedTreatment,
        activePrices.list[selectedTreatment]?.amount,
      );

      dispatch(addToCart({ product: convertedProduct, quantity }));
      setIsAddedToCart(true);

      // Reset the added state after 2 seconds
      setTimeout(() => {
        setIsAddedToCart(false);
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Product Details Section */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 2xl:px-8 py-8 sm:py-12 lg:py-16">
        {/* Product Header */}
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-8 sm:mb-10 lg:mb-12 pb-6 sm:pb-8 lg:pb-10 border-b border-gray-200">
          <div className="flex-1 pr-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 font-[family-name:var(--font-playfair)]">
              {product.productName}
            </h1>

            <p className="text-sm sm:text-base text-gray-600">
              {isAvailable
                ? `${activePrices.list.length} treatment option${
                    activePrices.list.length > 1 ? "s" : ""
                  } available`
                : "Not currently available to order"}
            </p>
          </div>

          {/* Price Badge */}
          <div className="mt-6 md:mt-0 md:ml-8 flex-shrink-0">
            {isAvailable ? (
              <div className="bg-[#7771B8] text-white px-4 py-3 md:px-8 md:py-6 rounded-lg shadow-lg">
                <div className="text-xs md:text-sm font-medium text-white/90 mb-1 md:mb-2">
                  Starting from
                </div>
                <div className="text-2xl md:text-4xl font-bold">
                  £{startingPrice}
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 text-gray-600 px-4 py-3 md:px-8 md:py-6 rounded-lg border border-gray-200">
                <div className="text-base md:text-lg font-semibold">
                  Currently unavailable
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
          {/* Left Column - Product Gallery */}
          <div>
            {/* Product Image */}
            <div className="mb-6 sm:mb-8">
              <div className="relative w-full aspect-square flex items-center justify-center overflow-hidden border border-gray-200 rounded-xl shadow-lg group">
                {product.productBase.leadImage ? (
                  <>
                    <Image
                      src={product.productBase.leadImage}
                      alt={product.productName}
                      width={600}
                      height={600}
                      className="object-contain w-full h-full group-hover:scale-[1.02] transition-transform duration-700"
                    />
                    {/* Subtle overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </>
                ) : (
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-8 text-center">
                    <div className="bg-gradient-to-r from-[#7771B8] to-[#6B5B9A] text-white text-sm font-bold py-2 px-4 -mt-8 mb-6 inline-block rounded-lg shadow-lg">
                      UKWEIGHTLOSS
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-3">
                      General Health
                    </div>
                    <div className="text-xl font-semibold text-gray-600 mb-6">
                      Blood test kit
                    </div>
                    <div className="text-sm text-gray-500 font-medium">
                      Get your results the next day
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Description */}
            <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-100">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">
                About this product
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {product.productBase.about}
              </p>
            </div>

            {/* Key Benefits */}
            <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-100">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-5">
                Key benefits
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(
                  product.productBase?.keyBenefits || [
                    "Medical Grade",
                    "Fast Delivery",
                    "Expert Support",
                    "Proven Results",
                  ]
                ).map((benefit, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-green-50 px-4 py-3 rounded-lg border border-green-100"
                  >
                    <Image src={checked} alt="Check" width={18} height={18} />
                    <span className="text-sm font-semibold text-gray-700">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Share */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-xs sm:text-sm font-semibold text-gray-700">
                Share product
              </span>
              <div className="flex gap-2 sm:gap-3">
                {[facebookIcon, twitterIcon, linkedinIcon, pinterestIcon].map(
                  (icon, idx) => (
                    <button
                      key={idx}
                      className="w-8 h-8 sm:w-9 sm:h-9 bg-white rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 hover:border-gray-300 transition-all duration-300 hover:scale-110 shadow-sm"
                    >
                      <Image
                        src={icon}
                        alt="Social"
                        width={14}
                        height={14}
                        className="opacity-70 sm:w-4 sm:h-4"
                      />
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Purchase Section */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            {/* Ordering options, shown only when the product offers both */}
            {showOfferChoice && (
              <div className="mb-6 sm:mb-8">
                <div className="bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-50 to-white px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                      Choose how you order
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Both give you the same medication. Pick the option that
                      suits you.
                    </p>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(
                        [
                          {
                            id: "standard" as OrderingPathway,
                            title: "Flexible ordering",
                            description:
                              offers.standardDescription ||
                              "Order when you need it, with full flexibility.",
                            prices: product.productPrices,
                          },
                          {
                            id: "lillydirect" as OrderingPathway,
                            title: "LillyDirect",
                            description:
                              offers.lillydirectDescription ||
                              "Lower cost, supplied and delivered by LillyDirect.",
                            prices: product.lillyDirectPrices,
                          },
                        ] as const
                      ).map((offer) => {
                        const isSelected = selectedOffer === offer.id;
                        const fromPrice = offer.prices.list.length
                          ? Math.min(
                              ...offer.prices.list.map((item) => item.amount)
                            )
                          : null;

                        return (
                          <button
                            key={offer.id}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => handleOfferChange(offer.id)}
                            className={`text-left rounded-lg p-4 transition-all duration-200 border-2 ${
                              isSelected
                                ? "bg-gradient-to-r from-[#7771B8]/5 to-[#6B5B9A]/5 border-[#7771B8] shadow-md"
                                : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm"
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <div
                                className={`relative w-5 h-5 border-2 rounded-full flex-shrink-0 transition-all duration-200 ${
                                  isSelected
                                    ? "border-[#7771B8] bg-[#7771B8]"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-2.5 h-2.5 bg-white rounded-full" />
                                  </div>
                                )}
                              </div>
                              <span className="font-semibold text-gray-900 text-sm sm:text-base">
                                {offer.title}
                              </span>
                            </div>
                            {fromPrice !== null && (
                              <div className="text-lg font-bold text-[#7771B8] mb-1">
                                From £{fromPrice}
                              </div>
                            )}
                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                              {offer.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* What LillyDirect actually commits the patient to. Shown here,
                before the CTA, because the commitments are the other half of
                the lower price. */}
            {showOfferChoice && selectedOffer === "lillydirect" && (
              <div className="mb-6 sm:mb-8">
                <div className="bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-gray-50 to-white px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                      LillyDirect
                    </h3>
                    {lillyMaxSaving ? (
                      <p className="text-sm font-semibold text-emerald-800 mt-1">
                        Cheaper on every dose, saving up to £{lillyMaxSaving}.
                      </p>
                    ) : null}
                  </div>
                  <div className="p-4 sm:p-6 space-y-5">
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-800 mb-2">
                        Benefits
                      </h4>
                      <ul className="list-disc pl-5 space-y-2 marker:text-emerald-700">
                        {LILLYDIRECT_BENEFITS.map((item) => (
                          <li
                            key={item}
                            className="text-sm text-gray-700 leading-relaxed"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        LillyDirect terms
                      </h4>
                      <ul className="list-disc pl-5 space-y-2 marker:text-gray-300">
                        {LILLYDIRECT_COMMITMENTS.map((item) => (
                          <li
                            key={item}
                            className="text-sm text-gray-700 leading-relaxed"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100 leading-relaxed">
                        You will be asked to confirm you agree to these before
                        placing a LillyDirect order.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Treatment Options */}
            <div className="mb-6 sm:mb-8">
              <div className="bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-white px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                    Select your treatment plan
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Choose the option that best fits your health goals
                  </p>
                </div>

                <div className="p-4 sm:p-6">
                  <div className="space-y-3">
                    {activePrices.list.map((option, index) => (
                      <div
                        key={index}
                        className={`group relative overflow-hidden rounded-lg transition-all duration-200 ${
                          selectedTreatment === index
                            ? "bg-gradient-to-r from-[#7771B8]/5 to-[#6B5B9A]/5 border-2 border-[#7771B8] shadow-md"
                            : "bg-white border-2 border-gray-200 hover:border-gray-300 hover:shadow-sm"
                        }`}
                        onClick={() => setSelectedTreatment(index)}
                      >
                        <div className="flex items-center p-4 sm:p-5 cursor-pointer">
                          {/* Custom Radio Button */}
                          <div className="flex-shrink-0 mr-3 sm:mr-4">
                            <div
                              className={`relative w-5 h-5 sm:w-6 sm:h-6 border-2 rounded-full transition-all duration-200 ${
                                selectedTreatment === index
                                  ? "border-[#7771B8] bg-[#7771B8]"
                                  : "border-gray-300 bg-white group-hover:border-gray-400"
                              }`}
                            >
                              {selectedTreatment === index && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full" />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Treatment Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 pr-3 sm:pr-4">
                                <h4
                                  className={`text-sm sm:text-base font-semibold leading-tight mb-1.5 ${
                                    selectedTreatment === index
                                      ? "text-gray-900"
                                      : "text-gray-800 group-hover:text-gray-900"
                                  }`}
                                >
                                  {option.label}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs sm:text-sm text-gray-500 font-medium">
                                    Per pack
                                  </span>
                                  {selectedTreatment === index && (
                                    <div className="inline-flex items-center px-2 py-0.5 bg-[#7771B8] text-white text-[10px] sm:text-xs font-bold rounded">
                                      Selected
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Price Display */}
                              <div className="text-right flex-shrink-0">
                                <div
                                  className={`text-xl sm:text-2xl font-bold mb-0.5 ${
                                    selectedTreatment === index
                                      ? "text-[#7771B8]"
                                      : "text-gray-900"
                                  }`}
                                >
                                  £{option.amount}
                                </div>
                                <div className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase">
                                  Incl. VAT
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Subtle selection highlight */}
                        {selectedTreatment === index && (
                          <div className="absolute top-3 right-3 sm:top-4 sm:right-4 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#7771B8] rounded-full" />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Treatment Summary */}
                  {activePrices.list[selectedTreatment] && (
                    <div className="mt-4 sm:mt-5 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Selected treatment
                          </div>
                          <div className="text-base sm:text-lg font-bold text-gray-900 line-clamp-1">
                            {
                              activePrices.list[selectedTreatment]
                                .label
                            }
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl sm:text-2xl font-bold text-[#7771B8]">
                            £
                            {
                              activePrices.list[selectedTreatment]
                                .amount
                            }
                          </div>
                          <div className="text-xs text-gray-500 font-medium">
                            Per pack
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quantity Selector - Premium */}
            {/* <div className="mb-8 pb-8 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-base font-bold text-gray-900 tracking-tight">
                    Quantity
                  </span>
                  <div className="flex items-center border-2 border-gray-200 shadow-sm">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-5 py-3 text-lg font-bold text-gray-700 hover:bg-gray-100 bg-gray-50 transition-colors duration-300"
                    >
                      −
                    </button>
                    <span className="px-6 py-3 text-gray-900 font-bold border-x-2 border-gray-200 min-w-[4rem] text-center bg-white">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-5 py-3 text-lg font-bold text-gray-700 hover:bg-gray-100 bg-gray-50 transition-colors duration-300"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 px-5 py-4 border border-gray-200">
                  <div className="text-xs text-gray-500 font-medium mb-1">
                    Package Details
                  </div>
                  <div className="text-sm text-gray-700 font-semibold mb-2">
                    {product.productBase.packageDetails ||
                      "1 pen (4 weeks supply)"}
                  </div>
                  <div className="text-xl font-bold text-[#7771B8]">
                    £
                    {activePrices.list[selectedTreatment]?.amount ||
                      activePrices.list[0]?.amount}
                    <span className="text-sm font-normal text-gray-500">
                      {" "}
                      /pack
                    </span>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Action Buttons */}
            <div ref={buttonRef} className="space-y-3 mb-6 sm:mb-8">
              {pgdFormRefId && isAvailable ? (
                <Link href={consultationHref}>
                  <button className="group relative w-full bg-gradient-to-r from-[#7771B8] to-[#6B5B9A] text-white py-4 sm:py-4 px-4 sm:px-6 text-sm sm:text-base font-semibold rounded-lg flex items-center justify-center gap-2 sm:gap-3 transition-all duration-300 hover:shadow-xl hover:shadow-[#7771B8]/30 shadow-lg overflow-hidden">
                    {/* Shine effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                    <Image
                      src={cartWhite}
                      alt="Cart"
                      width={20}
                      height={20}
                      className="relative group-hover:scale-110 transition-transform duration-300"
                    />
                    <span className="relative">Start consultation</span>
                  </button>
                </Link>
              ) : (
                <button
                  disabled
                  className="group relative w-full bg-gray-400 text-white py-4 sm:py-4 px-4 sm:px-6 text-sm sm:text-base font-semibold rounded-lg flex items-center justify-center gap-2 sm:gap-3 cursor-not-allowed opacity-60"
                  title="Consultation form not available for this product"
                >
                  <Image
                    src={cartWhite}
                    alt="Cart"
                    width={18}
                    height={18}
                    className="sm:w-5 sm:h-5"
                  />
                  <span className="relative">Consultation unavailable</span>
                </button>
              )}

              {/* <button
                onClick={handleAddToCart}
                disabled={false}
                className={`group/btn relative w-full py-5 px-6 font-bold flex items-center justify-center gap-3 transition-all duration-300 overflow-hidden ${
                  isAddedToCart
                    ? "bg-green-600 text-white border-2 border-green-600 shadow-lg shadow-green-600/25"
                    : "bg-white text-gray-900 border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-lg shadow-gray-200/50 hover:shadow-xl hover:shadow-gray-200/60"
                } ${false ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isAddedToCart ? (
                  <>
                    <Image src={checked} alt="Added" width={20} height={20} />
                    <span className="tracking-wide">Added to Cart!</span>
                  </>
                ) : (
                  <>
                    <Image
                      src={cartBlack}
                      alt="Cart"
                      width={20}
                      height={20}
                      className="group-hover/btn:scale-110 transition-transform duration-300"
                    />
                    <span className="tracking-wide">Add to Cart</span>
                  </>
                )}
              </button> */}
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Free shipping", sublabel: "On all orders" },
                { label: "Secure payment", sublabel: "256-bit SSL" },
                { label: "Expert support", sublabel: "24/7 assistance" },
                { label: "Money back", sublabel: "30-day guarantee" },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center"
                >
                  <div className="text-xs font-bold text-gray-900 mb-0.5">
                    {item.label}
                  </div>
                  <div className="text-xs text-gray-500">{item.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Product Features Section */}
      <ProductFeaturesSection />

      {/* Product Suitability Section */}
      <ProductSuitabilitySection product={product} />

      {/* Product Information Tabs Section */}
      <ProductInformationTabs product={product} />

      {/* Related Products Section */}
      {/* <RelatedProductsSection /> */}
      <ProcessSection />

      {/* Latest Blog Posts */}
      <ProductBlogSection
        posts={latestPosts}
        title={
          product.productName.length <= 24
            ? `${product.productName} Advice & Insights`
            : "Weight Loss Advice & Insights"
        }
        description="Evidence-based articles from our clinical team to help you get the best results from your treatment."
      />

      {/* Floating Consultation Button - Mobile Only */}
      {pgdFormRefId && isAvailable && showFloatingButton && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden">
          {/* Bottom gradient shadow for depth */}
          <div className="absolute inset-x-0 bottom-full h-8 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />

          <div className="bg-white border-t-2 border-gray-200 shadow-2xl px-4 py-3 animate-slideUp">
            <div className="max-w-lg mx-auto">
              <Link href={consultationHref}>
                <button className="group relative w-full bg-gradient-to-r from-[#7771B8] to-[#6B5B9A] text-white py-4 px-6 rounded-lg font-semibold flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-xl hover:shadow-[#7771B8]/30 shadow-lg overflow-hidden">
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />

                  <Image
                    src={cartWhite}
                    alt="Cart"
                    width={20}
                    height={20}
                    className="relative group-hover:scale-110 transition-transform duration-300"
                  />
                  <span className="relative">Start consultation</span>

                  {/* Price indicator */}
                  <span className="relative ml-auto text-sm font-bold bg-white/20 px-3 py-1 rounded-lg backdrop-blur-sm">
                    £
                    {activePrices.list[selectedTreatment]?.amount ||
                      activePrices.list[0]?.amount}
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
